import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import styled from "styled-components";
import { ConfirmationModal } from "./ConfirmationModal";

/* ---------- Color helpers ---------- */

const getMainColor = (props) =>
  props.$mainColor || "var(--widget-main-color, #ee754c)";

const getTextColor = (props) => (props.$widgetDark ? "#ffffff" : "#4f5f5b");

const getMutedTextColor = (props) =>
  props.$widgetDark ? "rgba(255, 255, 255, 0.7)" : "#7c8885";

const getBorderColor = (props) =>
  props.$widgetDark ? "rgba(255, 255, 255, 0.28)" : "rgba(153, 176, 173, 0.85)";

const getSoftBackground = (props) =>
  props.$widgetDark ? "rgba(255, 255, 255, 0.06)" : "transparent";

const getSoftHoverBackground = (props) =>
  props.$widgetDark ? "rgba(255, 255, 255, 0.12)" : "rgba(153, 176, 173, 0.14)";

/* ---------- Layout ---------- */

const OptionsBox = styled.div`
  width: 100%;
  height: ${({ $computedHeight }) =>
    $computedHeight ? `${$computedHeight}px` : "300px"};
  min-height: 90px;
  max-height: ${({ $computedHeight }) =>
    $computedHeight ? `${$computedHeight}px` : "300px"};
  box-sizing: border-box;

  display: flex;
  align-items: stretch;
  justify-content: center;

  overflow: hidden;
  background: transparent;
`;

const OptionsWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;

  padding: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(4px, 1vw, 8px)" : "10px"};

  display: grid;
  grid-template-columns: ${({ $widgetMode }) =>
    $widgetMode
      ? "repeat(3, minmax(0, 1fr))"
      : "repeat(auto-fit, minmax(80px, 1fr))"};

  grid-auto-rows: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(42px, 11vw, 58px)" : "60px"};

  gap: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(6px, 1.4vw, 10px)" : "14px"};

  align-content: start;
  justify-content: stretch;

  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  scrollbar-color: ${(props) => getMainColor(props)} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => getMainColor(props)};
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => getMainColor(props)};
  }
`;

const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(props) => getMutedTextColor(props)};
  font-weight: 700;
  text-align: center;
`;

const OptionBox = styled.button`
  width: 100%;
  height: 100%;
  min-width: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  position: relative;

  color: ${({ $selected, $disabled, $widgetDark }) => {
    if ($disabled) {
      return $widgetDark
        ? "rgba(255, 255, 255, 0.36)"
        : "rgba(79, 95, 91, 0.45)";
    }

    if ($selected) return "#ffffff";

    return $widgetDark ? "#ffffff" : "#4f5f5b";
  }};

  background: ${({ $selected, $disabled, $widgetDark, $mainColor }) => {
    if ($disabled) {
      return $widgetDark
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(153, 176, 173, 0.08)";
    }

    if ($selected) return $mainColor || "var(--widget-main-color, #ee754c)";

    return $widgetDark ? "rgba(255, 255, 255, 0.06)" : "transparent";
  }};

  border: 1px solid
    ${({ $selected, $disabled, $widgetDark, $mainColor }) => {
      if ($disabled) {
        return $widgetDark
          ? "rgba(255, 255, 255, 0.14)"
          : "rgba(153, 176, 173, 0.3)";
      }

      if ($selected) return $mainColor || "var(--widget-main-color, #ee754c)";

      return $widgetDark
        ? "rgba(255, 255, 255, 0.28)"
        : "rgba(153, 176, 173, 0.85)";
    }};

  box-shadow: ${({ $selected, $widgetDark }) =>
    $selected && $widgetDark ? "0 0 0 1px rgba(255, 255, 255, 0.12)" : "none"};

  border-radius: 8px;

  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.75 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};

  font-size: ${({ $widgetMode }) =>
    $widgetMode ? "clamp(0.78rem, 2.6vw, 0.95rem)" : "1rem"};
  font-weight: 800;
  line-height: 1;

  padding: ${({ $widgetMode }) => ($widgetMode ? "4px" : "6px")};
  box-sizing: border-box;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    color: ${({ $selected, $disabled, $widgetDark }) => {
      if ($disabled) {
        return $widgetDark
          ? "rgba(255, 255, 255, 0.36)"
          : "rgba(79, 95, 91, 0.45)";
      }

      if ($selected) return "#ffffff";

      return $widgetDark ? "#ffffff" : "#4f5f5b";
    }};

    border-color: ${({ $selected, $disabled, $mainColor }) => {
      if ($disabled) return undefined;
      if ($selected) return $mainColor || "var(--widget-main-color, #ee754c)";
      return $mainColor || "var(--widget-main-color, #ee754c)";
    }};

    background: ${({ $selected, $disabled, $widgetDark, $mainColor }) => {
      if ($disabled) {
        return $widgetDark
          ? "rgba(255, 255, 255, 0.04)"
          : "rgba(153, 176, 173, 0.08)";
      }

      if ($selected) return $mainColor || "var(--widget-main-color, #ee754c)";

      return $widgetDark
        ? "rgba(255, 255, 255, 0.12)"
        : "rgba(153, 176, 173, 0.14)";
    }};

    transform: ${({ $disabled }) => ($disabled ? "none" : "translateY(-1px)")};
  }
`;

const InnerLine = styled.div`
  width: 30%;
  height: ${({ $widgetMode }) => ($widgetMode ? "3px" : "4px")};
  border-radius: 999px;
  position: absolute;
  bottom: ${({ $widgetMode }) => ($widgetMode ? "7px" : "10px")};

  background-color: ${({
    $disabled,
    $isFull,
    $surpasses_people_limit,
    $from_restaurant,
    $past,
  }) => {
    if ($disabled) return "#d94b4b";
    if (($from_restaurant && $isFull) || $past) return "#c46127";
    if ($surpasses_people_limit) return "#d9c834";
    return "#41ab8b";
  }};
`;

const WarningCard = styled.div`
  background: #f1f3f4;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  color: #2d2d2d;
`;

const WarningTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
  color: #1e1e1e;
`;

const WarningText = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: #4a4f55;
`;

const CountText = styled.div`
  font-size: ${({ $widgetMode }) => ($widgetMode ? "9px" : "10px")};
  position: absolute;
  bottom: 0;
  color: currentColor;
`;

/* ---------- Helpers ---------- */

const getDateTimeInTimezone = (timezone) => {
  const now = new Date();
  const dateInTimezone = new Date(
    now.toLocaleString("en-US", { timeZone: timezone }),
  );

  const year = dateInTimezone.getFullYear();
  const month = String(dateInTimezone.getMonth() + 1).padStart(2, "0");
  const day = String(dateInTimezone.getDate()).padStart(2, "0");
  const hours = String(dateInTimezone.getHours()).padStart(2, "0");
  const minutes = String(dateInTimezone.getMinutes()).padStart(2, "0");

  return {
    dateObj: dateInTimezone,
    dateStr: `${year}-${month}-${day}`,
    timeStr: `${hours}:${minutes}`,
  };
};

const parseSlot = (token) => {
  const parts = token.split("/");
  const hhmm = parts[0];
  let maxPeopleFromToken = null;
  let flags = [];

  if (parts.length >= 2) {
    const maybeNumber = Number(parts[1]);

    if (!Number.isNaN(maybeNumber)) {
      maxPeopleFromToken = parts[1];
      flags = parts.slice(2);
    } else {
      flags = parts.slice(1);
    }
  }

  return { hhmm, maxPeopleFromToken, flags };
};

const formatDate = (date) => {
  if (!(date instanceof Date)) date = new Date(date);

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
};

const isTimeInPast = (slotDate, slotTimeStr, todayDate, nowTimeStr) => {
  const slotDateStr = formatDate(slotDate);
  const todayStr = formatDate(todayDate);

  if (slotDateStr < todayStr) return true;
  if (slotDateStr === todayStr && slotTimeStr < nowTimeStr) return true;

  return false;
};

const buildWarnings = ({
  isFull,
  surpassesLimit,
  past,
  slotMaxFromToken,
  showTableWarnings,
}) => {
  const list = [];

  if (showTableWarnings && isFull) {
    list.push({
      key: "no_tables",
      title: "Mesas indisponíveis",
      text:
        "Não há mesas disponíveis nesta sala e horário. \n" +
        "Ao continuar, não serão atribuídas mesas automaticamente.",
    });
  }

  if (showTableWarnings && surpassesLimit) {
    list.push({
      key: "people_limit",
      title: "Limite de pessoas por horário ultrapassado",
      text:
        `Cada horário disponível aceita até ${
          slotMaxFromToken ?? "?"
        } pessoas.\n` +
        "Ao continuar irá ultrapassar este limite pré-definido.\n\n" +
        "Esta definição pode ser alterada nos horários.",
    });
  }

  if (past) {
    list.push({
      key: "past_time",
      title: "Hora ultrapassada",
      text: "Já passa da hora selecionada.",
    });
  }

  return list;
};

const getClosestHeightLimit = (element) => {
  if (!element) return null;

  let node = element.parentElement;

  while (node && node !== document.body) {
    const styles = window.getComputedStyle(node);
    const overflowY = styles.overflowY;
    const rect = node.getBoundingClientRect();

    const hasHeight = rect.height > 0;
    const clipsContent =
      overflowY === "hidden" || overflowY === "auto" || overflowY === "scroll";

    if (hasHeight && clipsContent) {
      return rect.bottom;
    }

    node = node.parentElement;
  }

  return window.innerHeight;
};

/* ---------- Component ---------- */

function ChooseTime({
  options,
  onTimeChange,
  time,
  changeTime,
  currentZone,
  current_date,
  from_restaurant,
  people_per_time,
  timezone,
  widgetMode = false,
  widgetDark = false,
  mainColor,
}) {
  const [selectedTime, setSelectedTime] = useState(time || null);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [showModalWarning, setShowModalWarning] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [pendingBaseTime, setPendingBaseTime] = useState(null);
  const [computedHeight, setComputedHeight] = useState(widgetMode ? 180 : 300);

  const outerRef = useRef(null);
  const selectedTimeRef = useRef(null);
  const listContainerRef = useRef(null);
  const optionRefs = useRef({});

  const { dateStr: currentDate, timeStr: currentTime } =
    getDateTimeInTimezone(timezone);

  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!outerRef.current) return;

      if (!widgetMode) {
        setComputedHeight(300);
        return;
      }

      const rect = outerRef.current.getBoundingClientRect();
      const bottomLimit = getClosestHeightLimit(outerRef.current);

      if (!bottomLimit) return;

      const available = Math.floor(bottomLimit - rect.top - 6);
      const safeHeight = Math.max(90, available);

      setComputedHeight(safeHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);

    if (outerRef.current?.parentElement) {
      resizeObserver.observe(outerRef.current.parentElement);
    }

    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);

    const raf = requestAnimationFrame(updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      cancelAnimationFrame(raf);
    };
  }, [widgetMode, currentOptions.length, currentZone]);

  useEffect(() => {
    if (time !== selectedTime) setSelectedTime(time);
  }, [time]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (options && currentZone) {
      const timesInCurrentZone = options[currentZone] || [];
      setCurrentOptions(timesInCurrentZone);
    } else {
      setCurrentOptions([]);
    }

    optionRefs.current = {};
  }, [options, currentZone]);

  const isDisabledForUser = (token) => {
    const { hhmm, flags } = parseSlot(token);
    const hasB = flags.includes("b");
    const hasF = flags.includes("f");
    const hasPT = flags.includes("pt");

    const isPastClient = isTimeInPast(
      current_date,
      hhmm,
      currentDate,
      currentTime,
    );

    const past = hasPT || isPastClient;

    return !from_restaurant && (hasB || hasF || past);
  };

  useEffect(() => {
    if (!Array.isArray(currentOptions) || currentOptions.length === 0) return;

    const firstEnabledToken = currentOptions.find((t) => !isDisabledForUser(t));
    if (!firstEnabledToken) return;

    const el = optionRefs.current[firstEnabledToken];

    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    currentOptions,
    current_date,
    currentDate,
    currentTime,
    timezone,
    from_restaurant,
  ]);

  const handleTimeClick = (event, token) => {
    event.preventDefault();

    const { hhmm, maxPeopleFromToken, flags } = parseSlot(token);
    setPendingBaseTime(hhmm);

    const hasB = flags.includes("b");
    const hasF = flags.includes("f");
    const hasPT = flags.includes("pt");

    const isPastClient = isTimeInPast(
      current_date,
      hhmm,
      currentDate,
      currentTime,
    );

    const past = hasPT || isPastClient;

    const disabledForCustomer = !from_restaurant && (hasB || hasF || past);
    if (disabledForCustomer) return;

    const showTableWarnings = !!from_restaurant;
    const warningsToShow = buildWarnings({
      isFull: hasF,
      surpassesLimit: hasB,
      past,
      slotMaxFromToken: maxPeopleFromToken,
      showTableWarnings,
    });

    if (warningsToShow.length > 0) {
      setWarnings(warningsToShow);
      setShowModalWarning(true);
      return;
    }

    setSelectedTime(hhmm);
    changeTime(hhmm);
    onTimeChange(hhmm);
  };

  const handleConfirmWarning = () => {
    if (pendingBaseTime) {
      setSelectedTime(pendingBaseTime);
      changeTime(pendingBaseTime);
      onTimeChange(pendingBaseTime);
    }

    setShowModalWarning(false);
  };

  const confirmLabel =
    warnings.length === 1 && warnings[0].key === "past_time"
      ? "Continuar com a hora ultrapassada"
      : "Continuar";

  return (
    <OptionsBox
      ref={outerRef}
      $widgetMode={widgetMode}
      $computedHeight={computedHeight}
    >
      {Array.isArray(currentOptions) && currentOptions.length > 0 ? (
        <OptionsWrapper
          ref={listContainerRef}
          $widgetMode={widgetMode}
          $widgetDark={widgetDark}
          $mainColor={mainColor}
        >
          {currentOptions.map((token, index) => {
            const { hhmm, flags } = parseSlot(token);
            const hasB = flags.includes("b");
            const hasF = flags.includes("f");
            const hasPT = flags.includes("pt");

            const isPastClient = isTimeInPast(
              current_date,
              hhmm,
              currentDate,
              currentTime,
            );

            const past = hasPT || isPastClient;
            const isSelected = selectedTime === hhmm;
            const disabled = !from_restaurant && (hasB || hasF || past);

            const cleanCurrentZone = String(currentZone || "")
              .replace(/\/[fb]$/, "")
              .trim();

            const zonePeoplePerTime =
              people_per_time?.[cleanCurrentZone] &&
              typeof people_per_time?.[cleanCurrentZone] === "object"
                ? people_per_time[cleanCurrentZone]
                : null;

            const mappedCount =
              zonePeoplePerTime?.[hhmm] ?? people_per_time?.[hhmm] ?? 0;

            return (
              <OptionBox
                key={`${token}-${index}`}
                ref={(el) => {
                  optionRefs.current[token] = el;
                  if (isSelected) selectedTimeRef.current = el;
                }}
                type="button"
                onClick={(event) => handleTimeClick(event, token)}
                $disabled={disabled}
                $selected={isSelected}
                $widgetMode={widgetMode}
                $widgetDark={widgetDark}
                $mainColor={mainColor}
                title={disabled ? "Indisponível neste horário" : undefined}
              >
                <div>{hhmm}</div>

                {from_restaurant && mappedCount > 0 && (
                  <CountText $widgetMode={widgetMode}>
                    ({mappedCount})
                  </CountText>
                )}

                <InnerLine
                  $widgetMode={widgetMode}
                  $disabled={disabled}
                  $isFull={hasF}
                  $surpasses_people_limit={hasB}
                  $from_restaurant={from_restaurant}
                  $past={past}
                />
              </OptionBox>
            );
          })}
        </OptionsWrapper>
      ) : (
        <EmptyState $widgetDark={widgetDark}>Selecionar sala</EmptyState>
      )}

      <ConfirmationModal
        topSpacing="200px auto"
        show={showModalWarning}
        onHide={() => setShowModalWarning(false)}
        onConfirm={handleConfirmWarning}
        onCancel={() => setShowModalWarning(false)}
        title="Aviso"
        body={
          <div>
            {warnings.map((w) => (
              <WarningCard key={w.key}>
                <WarningTitle>{w.title}</WarningTitle>
                <WarningText>
                  {w.text.split("\n").map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </WarningText>
              </WarningCard>
            ))}
          </div>
        }
        confirmLabel={confirmLabel}
        cancelLabel="Cancelar"
        confirmColor={mainColor || "#EE754C"}
        cancelColor="#99B0AD"
      />
    </OptionsBox>
  );
}

export default ChooseTime;
